import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trash2, Minus, Plus, Tag, X, ShoppingCart, CalendarDays } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import AuthGate from '../AuthGate';
import Breadcrumbs from '../layout/Breadcrumbs';
import { toast } from 'sonner';
import { formatApiError } from '../../lib/apiError';

export default function CartPage() {
  return (
    <AuthGate>
      <CartPageContent />
    </AuthGate>
  );
}

function CartPageContent() {
  const { user } = useAuth();
  const { cart, fetchCart, updateQuantity, removeItem, applyCoupon, removeCoupon } = useCart();
  const [couponCode, setCouponCode] = useState('');
  const [applying, setApplying] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (user) fetchCart();
  }, [user, fetchCart]);

  const handleUpdateQty = async (productId, newQty) => {
    try {
      await updateQuantity(productId, Math.max(1, newQty));
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to update'));
    }
  };

  const handleRemove = async (productId) => {
    try {
      await removeItem(productId);
      toast.success('Item removed');
    } catch { }
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) return;
    setApplying(true);
    try {
      await applyCoupon(couponCode.trim());
      toast.success('Coupon applied!');
      setCouponCode('');
    } catch (err) {
      toast.error(formatApiError(err, 'Invalid coupon'));
    } finally {
      setApplying(false);
    }
  };

  if (!user) {
    return (
      <div className="pw-container py-16 text-center">
        <ShoppingCart size={48} className="mx-auto text-[#e7e3dd] mb-4" />
        <h2 className="text-lg font-semibold mb-2">Sign in to view your cart</h2>
        <Link to="/login" className="inline-block bg-[#2d2d2d] text-white px-6 py-2.5 rounded-lg text-sm">Sign In</Link>
      </div>
    );
  }

  const items = (cart?.items || []).slice().sort((a, b) => {
    const da = a.delivery_date || '';
    const db = b.delivery_date || '';
    return da.localeCompare(db);
  });

  return (
    <div className="pw-container py-2 pb-8">
      <Breadcrumbs items={[{ label: 'Cart' }]} />
      <h1 className="text-xl font-semibold tracking-wider uppercase mb-6">Shopping Cart</h1>

      {!!cart?.unavailable_items?.length && (
        <div role="alert" className="mb-5 rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm">
          {cart.unavailable_items.length} cart item(s) are no longer available. They were not removed automatically. Please remove them and review your order before checkout.
          {cart.unavailable_items.map(item => (
            <div key={`${item.source}-${item.product_id}`} className="mt-2 flex items-center gap-3">
              <span>Unavailable item {item.product_id}</span>
              <button type="button" onClick={() => handleRemove(item.product_id)} className="underline">Remove</button>
            </div>
          ))}
        </div>
      )}
      {cart?.coupon_error && <p role="alert" className="mb-5 rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm">{cart.coupon_error} Remove or replace the coupon before checkout.</p>}

      {items.length === 0 ? (
        <div className="text-center py-16">
          <ShoppingCart size={48} className="mx-auto text-[#e7e3dd] mb-4" />
          <p className="text-[#6f6a63] mb-4">Your cart is empty</p>
          <Link to="/shop" className="inline-block bg-[#2d2d2d] text-white px-6 py-2.5 rounded-lg text-sm tracking-wider uppercase">Continue Shopping</Link>
        </div>
      ) : (
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {items.map((item) => (
              <div key={item.product_id} className="flex gap-4 p-4 border border-[#e7e3dd] rounded-xl">
                <Link to={`/product/${item.product?.slug}`} className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0 bg-[#fbfaf8]">
                  <img src={item.product?.images?.[0] || item.product?.image_url || ''} alt={item.product?.name} className="w-full h-full object-cover" />
                </Link>
                <div className="flex-1 min-w-0">
                  <Link to={`/product/${item.product?.slug}`} className="text-sm font-semibold hover:text-[#2f6b4f] transition-colors">{item.product?.name}</Link>
                  <p className="text-xs text-[#6f6a63]">{item.product?.pack_size}</p>
                  <p className="text-xs text-[#6f6a63] mt-0.5">${item.unit_price?.toFixed(2)} per unit</p>
                  {item.delivery_date && (
                    <p className="flex items-center gap-1 text-[10px] text-[#2f6b4f] mt-1">
                      <CalendarDays size={10} />
                      Delivery: {new Date(item.delivery_date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                    </p>
                  )}
                  <div className="flex items-center justify-between mt-2">
                    <div className="flex items-center border border-[#e7e3dd] rounded-lg">
                      <button onClick={() => handleUpdateQty(item.product_id, item.quantity - 1)} className="p-1.5"><Minus size={14} /></button>
                      <span className="px-3 text-sm">{item.quantity}</span>
                      <button onClick={() => handleUpdateQty(item.product_id, item.quantity + 1)} className="p-1.5"><Plus size={14} /></button>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-semibold text-sm">${item.line_total?.toFixed(2)}</span>
                      <button onClick={() => handleRemove(item.product_id)} className="text-red-500 hover:text-red-700 p-1"><Trash2 size={14} /></button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="sticky top-28 border border-[#e7e3dd] rounded-xl p-6">
              <h2 className="text-sm font-semibold tracking-wider uppercase mb-4">Order Summary</h2>
              
              {/* Coupon */}
              <div className="mb-4">
                {cart.coupon_info ? (
                  <div className="flex items-center justify-between bg-[#e7f1ea] px-3 py-2 rounded-lg text-sm">
                    <span className="flex items-center gap-1"><Tag size={14} className="text-[#2f6b4f]" /> {cart.coupon_info.code}</span>
                    <button onClick={removeCoupon} className="text-red-500"><X size={14} /></button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <input
                      data-testid="cart-coupon-input"
                      type="text"
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value)}
                      placeholder="Coupon code"
                      className="flex-1 px-3 py-2 border border-[#e7e3dd] rounded-lg text-sm focus:outline-none focus:border-[#2f6b4f]"
                    />
                    <button
                      data-testid="cart-apply-coupon-button"
                      onClick={handleApplyCoupon}
                      disabled={applying}
                      className="px-4 py-2 bg-[#2d2d2d] text-white text-xs rounded-lg hover:bg-[#232323] disabled:opacity-50"
                    >
                      Apply
                    </button>
                  </div>
                )}
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-[#6f6a63]">Subtotal</span>
                  <span>${cart.subtotal?.toFixed(2)}</span>
                </div>
                {cart.volume_discount?.discount_amount > 0 && (
                  <div className="flex justify-between text-[#2f6b4f]">
                    <span>Volume Discount ({cart.volume_discount.discount_percent}%)</span>
                    <span>-${cart.volume_discount.discount_amount.toFixed(2)}</span>
                  </div>
                )}
                {cart.coupon_discount > 0 && (
                  <div className="flex justify-between text-[#2f6b4f]">
                    <span>Coupon Discount</span>
                    <span>-${cart.coupon_discount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-[#6f6a63]">Tax</span>
                  <span>{cart.tax_pending ? 'Pending tax policy' : `$${cart.tax?.toFixed(2)}`}</span>
                </div>
                <div className="border-t border-[#e7e3dd] pt-2 flex justify-between font-semibold text-base">
                  <span>Total</span>
                  <span>{cart.tax_pending ? 'Unavailable' : `$${cart.total?.toFixed(2)}`}</span>
                </div>
              </div>

              {/* Volume discount info */}
              {cart.volume_discount?.discount_percent === 0 && cart.subtotal < 500 && (
                <p className="text-xs text-[#6f6a63] mt-3 bg-[#fbfaf8] p-2 rounded">Spend ${(500 - cart.subtotal).toFixed(2)} more to get 3% off!</p>
              )}

              <button
                data-testid="cart-checkout-button"
                onClick={() => navigate('/checkout')}
                disabled={cart.checkout_blocked || !!cart.unavailable_items?.length}
                className="w-full mt-4 bg-[#2d2d2d] text-white py-3.5 rounded-lg text-sm tracking-wider uppercase font-medium hover:bg-[#232323] transition-colors active:scale-[0.98] disabled:opacity-50"
              >
                Proceed to Checkout
              </button>
              <Link to="/shop" className="block text-center text-xs text-[#6f6a63] mt-3 hover:text-[#2d2d2d]">Continue Shopping</Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
